import usePracticeExchange from "features/onboarding/guided/usePracticeExchange";
import {
  useOnboardingAnalytics,
  useOnboardingStepView,
} from "analytics/onboarding";
import useOnboardingPracticeAccount from "features/onboarding/guided/useOnboardingPracticeAccount";
import { useHistory, useLocation } from "react-router-dom";
import Select from "common/Select/Select";
import { ONBOARDING_PRACTICE_EXCHANGE_ID } from "testIds";
import { useTradingIntent } from "features/onboarding/guided/intent";
import { useContext, useEffect, useRef, useState } from "react";
import { Spin } from "common/Overlays/LoadingSpinnerOverlay";
import styled from "styled-components";
import { Alert } from "antd";
import { NextArrow } from "features/onboarding/common/OnboardingIcons";
import {
  cardSurface,
  softPanel,
  reveal,
} from "features/onboarding/common/onboardingUi";
import ArtStage from "features/onboarding/art/ArtStage";
import { newLookType } from "common/NewLook/tokens";
import { DemoThumb } from "features/onboarding/art/MethodThumbs";
import Button from "common/Buttons/Button";
import Text from "common/Typography/Text";
import { sharedLib } from "@shared/services/shared.service";
import { makeNameUnique } from "@shared/features/bots/utils";
import { useClientsData } from "@shared/utils/hooks/misc";
import { ClientData, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import {
  Choice,
  Eyebrow,
  HeadingBlock,
  HeroTitle,
  Lead,
  SectionTitle,
  SmallNote,
  WelcomeGrid,
  ListStepLayout,
} from "features/onboarding/guided/GuidedWelcome";
import {
  ONBOARDING_CREATE_PRACTICE_ID,
  ONBOARDING_PRACTICE_ERROR_ID,
} from "testIds";

const GuidedDemoSetup = () => {
  const search = new URLSearchParams(useLocation().search);
  const automatic = search.get("prepareDemo") === "1";
  // The same screen opens on its own (setting up the demo) or because the
  // user asked to change the demo exchange; keep the two apart.
  useOnboardingStepView("practice_account", {
    reason:
      search.get("changePractice") === "1" ? "change_exchange" : "automatic",
  });
  const track = useOnboardingAnalytics();
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const {
    startPractice,
    isLoading,
    hasError,
    exchange,
    exchangeDisplay,
    options,
    selectExchange,
    exchangesLoading,
    existingAccount,
    retryExchanges,
    accountsReady,
    accountsError,
    retryAccounts,
  } = useCreatePracticeAccount();
  const started = useRef(false);
  useEffect(() => {
    if (accountsError)
      track("Step Failed", {
        step: "practice_account",
        error_code: "accounts_load_failed",
      });
  }, [accountsError]);
  useEffect(() => {
    if (
      !automatic ||
      started.current ||
      exchangesLoading ||
      !options.length ||
      !accountsReady
    )
      return;
    started.current = true;
    void startPractice();
  }, [automatic, exchangesLoading, options.length, accountsReady]);
  if (accountsError)
    return (
      <ListStepLayout>
        <Alert
          type="error"
          role="alert"
          description={t("guided.account_error")}
          action={<Button onClick={retryAccounts}>{t("intent.retry")}</Button>}
        />
      </ListStepLayout>
    );
  if (automatic && !hasError && (exchangesLoading || options.length > 0))
    return <DemoPreparation exchangeDisplay={exchangeDisplay} />;
  return (
    <ListStepLayout>
      <HeadingBlock>
        <Eyebrow>{t("guided.demo_badge")}</Eyebrow>
        <HeroTitle>{t("guided.setup_title")}</HeroTitle>
        <Lead>{t("guided.setup_description")}</Lead>
      </HeadingBlock>
      <WelcomeGrid>
        <AccountChoice>
          <SetupStage height={190} phoneHeight={176}>
            <DemoThumb />
          </SetupStage>
          <DesktopEyebrow>{t("guided.virtual_only")}</DesktopEyebrow>
          <SectionTitle>{t("guided.account_title")}</SectionTitle>
          <Text>
            {t("intent.prepared_exchange", { exchange: exchangeDisplay })}
          </Text>
          {existingAccount ? (
            <SectionTitle>{t("intent.account_ready")}</SectionTitle>
          ) : (
            <BalanceFigure>$10,000 USD</BalanceFigure>
          )}
          <Text type="medium">
            {t(
              existingAccount
                ? "intent.reuse_account"
                : "guided.balance_description"
            )}
          </Text>
          <div style={{ height: 20 }} />
          {hasError && (
            <Alert
              data-testid={ONBOARDING_PRACTICE_ERROR_ID}
              type="error"
              role="alert"
              description={t("guided.account_error")}
              style={{ marginBottom: 16 }}
            />
          )}
          <Button
            type="primary"
            size="large"
            loading={isLoading}
            disabled={exchangesLoading || !options.length}
            onClick={startPractice}
            data-testid={ONBOARDING_CREATE_PRACTICE_ID}
          >
            {t(automatic ? "guided.prepare_account" : "intent.try_demo")}
            <NextArrow />
          </Button>
        </AccountChoice>
        <OptionalChoice>
          <DesktopEyebrow>{t("intent.exchange_optional")}</DesktopEyebrow>
          <SectionTitle>{t("intent.simple_exchange_title")}</SectionTitle>
          <Text>{t("intent.simple_exchange_description")}</Text>
          <FieldLabel
            htmlFor={ONBOARDING_PRACTICE_EXCHANGE_ID}
            style={{ margin: "20px 0 6px" }}
          >
            <Text>{t("intent.demo_exchange_label")}</Text>
          </FieldLabel>
          {!exchangesLoading && !options.length && (
            <Alert
              type="error"
              role="alert"
              description={t("intent.exchanges_error")}
              action={
                <Button onClick={retryExchanges}>{t("intent.retry")}</Button>
              }
              style={{ marginBottom: 16 }}
            />
          )}
          <Select
            id={ONBOARDING_PRACTICE_EXCHANGE_ID}
            data-testid={ONBOARDING_PRACTICE_EXCHANGE_ID}
            value={exchange}
            loading={exchangesLoading}
            disabled={exchangesLoading || isLoading}
            onChange={selectExchange}
            mobileTitle={t("intent.demo_exchange_label")}
            options={options.map((option) => ({
              value: option.exchange,
              label: (
                <Text
                  data-testid={`${ONBOARDING_PRACTICE_EXCHANGE_ID}-${option.exchange}`}
                >
                  {option.exchange_display}
                </Text>
              ),
            }))}
          />
          <SmallNote style={{ textAlign: "left", marginTop: 16 }}>
            {t("intent.one_practice_account")}
          </SmallNote>
          <FreedomNote style={{ textAlign: "left" }}>
            {t("intent.exchange_freedom")}
          </FreedomNote>
        </OptionalChoice>
      </WelcomeGrid>
    </ListStepLayout>
  );
};

export default GuidedDemoSetup;

const DemoPreparation = ({ exchangeDisplay }: { exchangeDisplay: string }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const [showFeedback, setShowFeedback] = useState(false);
  useEffect(() => {
    const timeout = window.setTimeout(() => setShowFeedback(true), 350);
    return () => window.clearTimeout(timeout);
  }, []);
  return (
    <ListStepLayout>
      {showFeedback && (
        <PreparationCard role="status" aria-live="polite">
          <Spin size="large" aria-hidden />
          <SectionTitle>{t("intent.preparing_demo")}</SectionTitle>
          <Text type="medium">{exchangeDisplay}</Text>
        </PreparationCard>
      )}
    </ListStepLayout>
  );
};

const useCreatePracticeAccount = () => {
  const track = useOnboardingAnalytics();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const { changeStep } = useContext(OnboardingContext)!;
  const { notDeletedClientsData, getClientsListStatus } = useClientsData();
  const { intent: selectedIntent } = useTradingIntent();
  const intent = selectedIntent || "dca";
  const { isFetching: exchangesLoading, refetch: retryExchanges } =
    sharedLib.sharedApi.configuration.useGetExchangesListQuery({
      nested_exchanges: true,
    });
  const location = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(location.search);
  const { options, selected, exchange, exchangeDisplay } =
    usePracticeExchange();
  const {
    account: existingAccount,
    name: accountName,
    storageKey,
    rememberAccount,
  } = useOnboardingPracticeAccount(exchange);
  const selectExchange = (next: string): void => {
    if (!options.some((option) => option.exchange === next)) return;
    if (next !== exchange) track("Demo Exchange Changed", { exchange: next });
    query.set("exchange", next);
    history.replace({ ...location, search: query.toString() });
    setHasError(false);
  };
  const [addClient, { isLoading }] =
    sharedLib.sharedApi.configuration.useAddClientMutation();
  const [refreshUser] =
    sharedLib.sharedApi.authentication.useLazyGetUserQuery();
  const [refreshAccounts] =
    sharedLib.sharedApi.configuration.useLazyGetClientsListQuery();
  const [hasError, setHasError] = useState(false);
  const startPractice = async (): Promise<void> => {
    if (!selected || exchangesLoading) return;
    if (!automaticSetup(query)) {
      changeStep(
        intent === "dca"
          ? OnboardingDetailedStep.COPY_DCA_BOT
          : OnboardingDetailedStep.TRADING_METHOD,
        {
          guided: "1",
          exchange,
          "account-id": "",
          prepareDemo: "",
          changePractice: "",
          setup: "",
          browse: "",
        }
      );
      return;
    }
    setHasError(false);
    try {
      let account = existingAccount;
      if (!account) {
        const request = {
          api_key: "",
          api_secret: "",
          exchange_to_client_name: {
            [exchange]: makeNameUnique(
              accountName,
              Object.fromEntries(
                (notDeletedClientsData || []).map((account) => [
                  account.client_id,
                  account.client_name,
                ])
              )
            ),
          },
          demo_initial_funds: 10000,
          is_demo_query: true,
        };
        let preparation = pendingPracticeAccounts.get(storageKey);
        if (!preparation) {
          preparation = addClient(request)
            .unwrap()
            .then((response) => {
              const created = response.data.data.find(
                (client) => client.exchange === exchange
              );
              if (!created)
                throw new Error("Practice account was not returned");
              rememberAccount(created);
              return created;
            })
            .finally(() => pendingPracticeAccounts.delete(storageKey));
          pendingPracticeAccounts.set(storageKey, preparation);
        }
        account = await preparation;
      }
      rememberAccount(account);
      await Promise.all([
        refreshUser({ is_demo_query: true }).unwrap(),
        refreshAccounts().unwrap(),
      ]);
      if (!mounted.current) return;
      track("Practice Account Ready", {
        trading_method: intent,
        trading_mode: "demo",
        exchange,
        reused: !!existingAccount,
      });
      const settingUp = query.get("setup") === "1";
      const copyingBot = intent === "dca" && !!query.get("botUUID");
      changeStep(
        copyingBot
          ? OnboardingDetailedStep.DCA_BOT_OVERVIEW
          : settingUp && intent === "signals"
          ? OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP
          : intent === "dca" && !settingUp
          ? OnboardingDetailedStep.COPY_DCA_BOT
          : OnboardingDetailedStep.TRADING_METHOD,
        {
          guided: settingUp && intent === "signals" ? "0" : "1",
          setup: settingUp && intent !== "signals" && !copyingBot ? "1" : "",
          method: intent,
          exchange,
          "account-id": String(account.client_id),
          prepareDemo: "",
          changePractice: "",
          browse: "",
        }
      );
    } catch {
      if (mounted.current)
        track("Step Failed", {
          step: "practice_account",
          error_code: "practice_account_failed",
        });
      if (mounted.current) setHasError(true);
    }
  };
  return {
    startPractice,
    isLoading,
    hasError,
    exchange,
    exchangeDisplay,
    options,
    selectExchange,
    exchangesLoading,
    existingAccount,
    retryExchanges,
    accountsReady:
      getClientsListStatus.isSuccess && !getClientsListStatus.isFetching,
    accountsError: getClientsListStatus.isError,
    retryAccounts: getClientsListStatus.refetch,
  };
};

const automaticSetup = (query: URLSearchParams): boolean =>
  query.get("setup") === "1" || query.get("prepareDemo") === "1";

const pendingPracticeAccounts = new Map<string, Promise<ClientData>>();

const PreparationCard = styled.div`
  ${cardSurface};
  ${reveal(0)};
  width: 420px;
  max-width: 100%;
  margin: 48px auto;
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  h2 {
    margin-top: 20px;
  }
`;
const SetupStage = styled(ArtStage)`
  margin-bottom: 20px;
  /* Phones: the choice comes first, without the picture (see below). */
  @media (max-width: 560px) {
    display: none;
  }
`;
// The optional exchange choice reads as a secondary, tinted panel, with its
// closing note pinned to the bottom so the two columns end together.
const OptionalChoice = styled(Choice)`
  ${softPanel};
  padding: 24px;
  @media (max-width: 480px) {
    padding: 16px;
  }
  /* Phones: the exchange choice first (it's what "Change" opens), then the
     practice account with its button; the labels the heading covers go. */
  @media (max-width: 560px) {
    order: -1;
  }
`;
const AccountChoice = styled(Choice)``;
/** A card's label the phone heading already covers ("Demo only", "Optional"). */
const DesktopEyebrow = styled(Eyebrow)`
  @media (max-width: 560px) {
    display: none;
  }
`;
const FreedomNote = styled(SmallNote)`
  && {
    margin-top: auto;
    padding-top: 24px;
  }
`;
// The starting balance is the card's figure.
const BalanceFigure = styled.div`
  margin: 16px 0 4px;
  ${newLookType.figureLarge};
  color: ${(p) => p.theme.colors.titleColor};
`;
// The select's label in the label role.
const FieldLabel = styled.label`
  && span {
    ${newLookType.label};
    color: ${(p) => p.theme.colors.secondaryText};
  }
`;
